import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const DraggableHeaderWrapper = styled.div<{ $is_dragging: boolean; $width?: number }>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  min-width: 0;
  user-select: none;

  ${({ $is_dragging, theme, $width }) =>
    $is_dragging &&
    css`
      opacity: 0.5;
      background-color: ${theme.colors.surface.base.hover.neutral};
      ${$width &&
      css`
        width: ${$width}px;
      `}
    `}
`;

const DragHandle = styled.div<{ $is_dragging: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  cursor: ${({ $is_dragging }) => ($is_dragging ? 'grabbing' : 'grab')};
  touch-action: none;

  &:hover {
    background-color: ${({ theme, $is_dragging }) =>
      !$is_dragging && theme.colors.surface.base.hover.neutral};
  }
`;

const ResizeHandle = styled.div<{ $is_resizing: boolean }>`
  position: absolute;
  top: 0;
  right: -3px;
  bottom: 0;
  width: 6px;
  z-index: 10;
  cursor: col-resize;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 2px;
    height: 100%;
    background-color: transparent;
    transition: background-color 0.15s ease;
  }

  &:hover::after,
  ${({ $is_resizing }) =>
      $is_resizing &&
      css`
      &::after,
    `}
    &:active::after {
    background-color: ${({ theme }) => theme.colors.outline.focus.primary};
  }
`;

const ContentWrapper = styled.div<{
  $full_page: boolean;
  $is_first: boolean;
  $is_last: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  height: 100%;
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(2)};
  /* Content should not block drag handle */
  pointer-events: none;

  ${({ theme, $full_page, $is_first, $is_last }) =>
    $full_page &&
    css`
      ${$is_first &&
      css`
        padding-left: ${theme.spacing(6)};
      `}
      ${$is_last &&
      css`
        padding-right: ${theme.spacing(6)};
      `}
    `}

  /* Re-enable pointer events for interactive elements and prevent drag on them */
  button,
  a {
    pointer-events: auto;
    touch-action: auto;
  }
`;

interface DraggableColumnHeaderProps {
  id: string;
  children: React.ReactNode;
  disabled?: boolean;
  full_page?: boolean;
  is_first?: boolean;
  is_last?: boolean;
  configured_width?: number;
  resizable?: boolean;
  column_index?: number;
  stored_width?: number;
  is_any_resizing?: boolean;
  onResizeStart?: (column_index: number) => void;
  onResize?: (column_index: number, new_width: number) => void;
  onResizeEnd?: (column_index: number, new_width: number) => void;
}

const MIN_STORED_WIDTH = 100;
const MAX_STORED_WIDTH = 800;

const DraggableColumnHeader: React.FC<DraggableColumnHeaderProps> = ({
  id,
  children,
  disabled = false,
  full_page = false,
  is_first = false,
  is_last = false,
  configured_width,
  resizable = false,
  column_index,
  stored_width,
  is_any_resizing = false,
  onResizeStart,
  onResize,
  onResizeEnd,
}) => {
  const [wrapper_node, setWrapperNode] = useState<HTMLDivElement | null>(null);
  const [original_width, setOriginalWidth] = useState<number | undefined>(configured_width);
  const [is_resizing, setIsResizing] = useState(false);

  const resize_state_ref = useRef<{
    start_x: number;
    start_width: number;
    raf_id: number | null;
  } | null>(null);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: disabled || is_any_resizing,
  });

  useEffect(() => {
    // Only measure width if no configured_width is provided
    if (!configured_width && wrapper_node && !original_width) {
      setOriginalWidth(wrapper_node.offsetWidth);
    }
  }, [original_width, configured_width, wrapper_node]);

  const set_refs = (node: HTMLDivElement | null) => {
    setWrapperNode(node);
    setNodeRef(node);
  };

  const handlePointerMove = (e: PointerEvent) => {
    if (!resize_state_ref.current || column_index === undefined) return;

    const { start_x, start_width, raf_id } = resize_state_ref.current;
    if (raf_id !== null) {
      cancelAnimationFrame(raf_id);
    }

    resize_state_ref.current.raf_id = requestAnimationFrame(() => {
      const delta = e.clientX - start_x;
      const new_width = Math.min(MAX_STORED_WIDTH, Math.max(MIN_STORED_WIDTH, start_width + delta));
      onResize?.(column_index, new_width);
      resize_state_ref.current!.raf_id = null;
    });
  };

  const handlePointerUp = (e: PointerEvent) => {
    if (!resize_state_ref.current || column_index === undefined) return;

    const { start_x, start_width, raf_id } = resize_state_ref.current;
    if (raf_id !== null) {
      cancelAnimationFrame(raf_id);
    }

    const delta = e.clientX - start_x;
    const final_width = Math.min(MAX_STORED_WIDTH, Math.max(MIN_STORED_WIDTH, start_width + delta));

    setIsResizing(false);
    resize_state_ref.current = null;
    document.removeEventListener('pointermove', handlePointerMove);
    document.removeEventListener('pointerup', handlePointerUp);
    onResizeEnd?.(column_index, final_width);
  };

  const handleResizePointerDown = (e: React.PointerEvent) => {
    if (column_index === undefined || stored_width === undefined) return;
    e.stopPropagation();
    e.preventDefault();

    setIsResizing(true);
    resize_state_ref.current = {
      start_x: e.clientX,
      start_width: stored_width,
      raf_id: null,
    };

    onResizeStart?.(column_index);
    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (resize_state_ref.current?.raf_id != null) {
        cancelAnimationFrame(resize_state_ref.current.raf_id);
      }
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
    };
  }, [handlePointerMove, handlePointerUp]);

  if (disabled) {
    return <>{children}</>;
  }

  return (
    <DraggableHeaderWrapper
      ref={set_refs}
      $is_dragging={isDragging}
      $width={isDragging ? original_width : undefined}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}>
      <DragHandle $is_dragging={isDragging} {...attributes} {...listeners} />
      <ContentWrapper $full_page={full_page} $is_first={is_first} $is_last={is_last}>
        {children}
      </ContentWrapper>
      {resizable && (
        <ResizeHandle $is_resizing={is_resizing} onPointerDown={handleResizePointerDown} />
      )}
    </DraggableHeaderWrapper>
  );
};

export default DraggableColumnHeader;
